import React, { useState, useEffect } from 'react';
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableFooter,
	Stack,
	Grid2,
	TextField,
	InputAdornment,
	IconButton,
	Button,
} from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify";
import Header from '../Header';
import Footer from '../Footer';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import { startOfMonth, endOfMonth } from 'date-fns';
import { useTable } from "../Minimal/src/hooks/use-table";
import { TablePaginationCustom } from "../Minimal/src/components/pagination/table-pagination";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const SalesRepReport = () => {
	const today = new Date();
	const defaultStart = startOfMonth(today);
	const defaultEnd = endOfMonth(today);
	const [dates, setDates] = useState([defaultStart, defaultEnd]);

	const [reportDate, setReportDate] = useState('');
	const [salesRapReport, setSalesRapReport] = useState({ data: [], order_amt: 0, accept_amt: 0, picking_amt: 0, deliver_amt: 0, invoice_amt: 0, return_amt: 0, total_tax: 0 });
	const [searchQuery, setSearchQuery] = useState('');
	const accessToken = localStorage.getItem('accessToken');
	const token = localStorage.getItem("token");
	const company_tax_type = localStorage.getItem('company_tax_type');
	const table = useTable();

	const handleDateChange = (value) => {
		setDates(value);
		if (value && value[0] && value[1]) {
			searchFormData(value);
		}
	};

	const searchFormData = (value) => {
		let startDate = value[0] ? format(value[0], 'dd/MM/yyyy') : '';
		let endDate = value[1] ? format(value[1], 'dd/MM/yyyy') : '';
		const range = startDate ? `${startDate} - ${endDate}` : '';
		fetchReport(range);
	};

	const fetchReport = async (range) => {
		const settings = await getCompanySettings();
		const useEncrypt = resolveCustomerApiEncrypt(settings);
		const formData = new FormData();
		const plain = { draw: 1, start: 0, length: 10000, reportrange: range };
		if (useEncrypt) {
			const ep = encryptPayload(plain);
			if (ep) {
				formData.append('encrypted_payload', ep);
			} else {
				Object.entries(plain).forEach(([k, v]) => formData.append(k, v));
			}
		} else {
			Object.entries(plain).forEach(([k, v]) => formData.append(k, v));
		}

		const apiUrl = `${config.API_URL}/staff_report_show`;
		try {
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					authorization: `Bearer ${token}`,
				},
				body: formData,
			});
			if (!response.ok) throw new Error('Failed to fetch report');
			const raw = await response.json();
			const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
			setSalesRapReport(data || { data: [], order_amt: 0, accept_amt: 0, picking_amt: 0, deliver_amt: 0, invoice_amt: 0, return_amt: 0, total_tax: 0 });
			setReportDate(`${data.startdate || ''} To Date : ${data.enddate || ''}`);
		} catch (error) {
		}
	};

	useEffect(() => {
		if (accessToken === null) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}
		searchFormData([defaultStart, defaultEnd]);
	}, []);


	const formatNumber = (num) => {
		return parseFloat(num || 0).toFixed(2);
	};

	const filteredData = React.useMemo(() => {
		if (!searchQuery) return salesRapReport.data || [];
		const query = searchQuery.toLowerCase();
		return (salesRapReport.data || []).filter((row) => {
			return (row.name || '').toLowerCase().includes(query);
		});
	}, [salesRapReport.data, searchQuery]);

	const footerTotals = React.useMemo(() => {
		if (!searchQuery) {
			return {
				order_amt: salesRapReport.order_amt,
				accept_amt: salesRapReport.accept_amt,
				picking_amt: salesRapReport.picking_amt,
				deliver_amt: salesRapReport.deliver_amt,
				invoice_amt: salesRapReport.invoice_amt,
				return_amt: salesRapReport.return_amt,
				total_tax: salesRapReport.total_tax,
			};
		}
		const toNum = (v) => parseFloat(String(v ?? 0).replace(/,/g, "")) || 0;
		const sumField = (field) =>
			filteredData.reduce((sum, row) => sum + toNum(row?.[field]), 0);
		return {
			order_amt: formatNumber(sumField("order_amt")),
			accept_amt: formatNumber(sumField("accept_amt")),
			picking_amt: formatNumber(sumField("picking_amt")),
			deliver_amt: formatNumber(sumField("deliver_amt")),
			invoice_amt: formatNumber(sumField("invoice_amt")),
			return_amt: formatNumber(sumField("return_amt")),
			total_tax: formatNumber(sumField("total_tax")),
		};
	}, [searchQuery, filteredData, salesRapReport]);

	const tableData = filteredData;
	const paginatedData = tableData.slice(
		table.page * table.rowsPerPage,
		table.page * table.rowsPerPage + table.rowsPerPage
	);

	return (
		<>
			{/* <Header /> */}
			<Box className="page-container">
				<Paper
					className="paper-style"
				>
					<Box sx={{ px: 2, py: 1.2 }}>
						<Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
							<Grid2 size={{ xs: 12, md: 4 }}>
								<Typography className="page-title">Sales Report</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }} justifyContent="end">
								<CommonDatePicker
									className="input-border"
									value={dates}
									showOneCalendar
									onChange={handleDateChange}
									format="dd/MM/yyyy"
									character=" - "
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }} justifyContent="end">
								<TextField
									className="input-border"
									fullWidth
									placeholder="Search by Sales Rep..."
									value={searchQuery}
									onChange={(e) => {
										setSearchQuery(e.target.value);
										table.setPage(0);
									}}
									size="small"
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Iconify icon="eva:search-outline" width={20} sx={{ color: "#637381" }} />
												</InputAdornment>
											),
											endAdornment: searchQuery && (
												<InputAdornment position="end">
													<IconButton
														size="small"
														onClick={() => {
															setSearchQuery('');
															table.setPage(0);
														}}
														sx={{ color: "#637381" }}
													>
														<Iconify icon="eva:close-fill" width={20} />
													</IconButton>
												</InputAdornment>
											),
										},
									}}
									sx={{
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#f5f7fa",
											fontSize: "0.875rem",
											"&:hover": {
												backgroundColor: "#fff",
											},
											"&.Mui-focused": {
												backgroundColor: "#fff",
											},
										},
									}}
								/>
							</Grid2>
						</Grid2>
					</Box>
					<TableContainer>
						<Table size="medium"
							className="table-cell-border"
							sx={{ tableLayout: "fixed" }}>
							<TableHead>
								<TableRow
									className="table-cell-header"
								>
									<TableCell
								    width="200px"
									>
										Sales Rep
									</TableCell>
									<TableCell
									width="100px"
									>
										Order
									</TableCell>
									<TableCell

									width="100px"
									>
										Accept
									</TableCell>
									<TableCell

									width="100px"
									>
										Picking
									</TableCell>
									<TableCell

									width="100px"
									>
										Delivered
									</TableCell>
									<TableCell

									width="100px"
									>
										Invoice
									</TableCell>
									<TableCell

									width="100px"
									>
										Return
									</TableCell>
									<TableCell

									width="100px"
									>
										{company_tax_type}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedData.length === 0 ? (
									<TableRow>
										<TableCell colSpan={8} sx={{ textAlign: "center", py: 3, color: "#637381" }}>
											No data available
										</TableCell>
									</TableRow>
								) : (
									paginatedData.map((row, index) => (
										<TableRow
											key={index}
											className="table-cell-style"
											sx={{
												"&:hover": {
													backgroundColor: "rgba(145, 158, 171, 0.08)",
												},

											}}
										>
											<TableCell sx={{ textAlign: "left !important" }}>
												{row.name || ""}
											</TableCell>
											<TableCell

											>
												{row.order_amt}
											</TableCell>
											<TableCell

											>
												{row.accept_amt}
											</TableCell>
											<TableCell
											>
												{row.picking_amt}
											</TableCell>
											<TableCell
											>
												{row.deliver_amt}
											</TableCell>
											<TableCell
											>
												{row.invoice_amt}
											</TableCell>
											<TableCell
											>
												{row.return_amt}
											</TableCell>
											<TableCell
											>
												{row.total_tax}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
							<TableFooter>
								<TableRow 
								className="table-cell-style"
								sx={{
									"& .MuiTableCell-root": {
										fontWeight: 600,
									},
								}}>
									<TableCell
									width="100px"
									align="right"
									>
										Total
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.order_amt}
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.accept_amt}
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.picking_amt}
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.deliver_amt}
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.invoice_amt}
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.return_amt}
									</TableCell>
									<TableCell
									width="100px"
									>
										{footerTotals.total_tax}
									</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>				
						<TablePaginationCustom
							count={tableData.length}
							page={table.page}
							rowsPerPage={table.rowsPerPage}
							onPageChange={table.onChangePage}
							onRowsPerPageChange={table.onChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50]}
							className="pagination-styles"
						/>
				</Paper>
			</Box>
		</>
	);
};

export default SalesRepReport;
